'use client'

import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { MdArrowBack, MdSave, MdInventory, MdSearch } from 'react-icons/md'
import { useAuth } from '@/store/hooks'
import {
    createBillingBatch as createBatch,
    updateBillingBatch as updateBatch,
    getBillingBatch as getBatch,
    CreateBillingBatchData,
    UpdateBillingBatchData,
} from '@/lib/api/billing/billing-batch-api'
import { getGuides, TissGuide } from '@/lib/api/billing/guide-api'
import { getHealthPlans, HealthPlan } from '@/lib/api/medical/health-plan-api'
import { format } from 'date-fns'

interface FormData {
    healthPlanId: string
    name: string
    referenceMonth: string
    dueDate: string
    notes: string
}

export default function BatchFormPage() {
    const { user } = useAuth()
    const navigate = useNavigate()
    const { id } = useParams<{ id: string }>()
    const isEditing = !!id

    const [loading, setLoading] = useState(false)
    const [loadingData, setLoadingData] = useState(false)
    const [loadingGuides, setLoadingGuides] = useState(false)
    const [healthPlans, setHealthPlans] = useState<HealthPlan[]>([])
    const [availableGuides, setAvailableGuides] = useState<TissGuide[]>([])
    const [selectedGuideIds, setSelectedGuideIds] = useState<number[]>([])
    const [searchTerm, setSearchTerm] = useState('')

    const [formData, setFormData] = useState<FormData>({
        healthPlanId: '',
        name: '',
        referenceMonth: format(new Date(), 'yyyy-MM'),
        dueDate: '',
        notes: '',
    })

    useEffect(() => {
        if (user?.companyId) {
            loadHealthPlans()
            if (isEditing && id) {
                loadBatch()
            }
        }
    }, [id, isEditing, user?.companyId])

    useEffect(() => {
        if (formData.healthPlanId && !isEditing) {
            loadAvailableGuides()
        }
    }, [formData.healthPlanId])

    const loadHealthPlans = async () => {
        if (!user?.companyId) return
        try {
            const response = await getHealthPlans(user.organizationId)
            if (response.success) {
                setHealthPlans(response.result || [])
            }
        } catch (error) {
            console.error('Erro ao carregar convênios:', error)
        }
    }

    const loadAvailableGuides = async () => {
        if (!formData.healthPlanId) return

        setLoadingGuides(true)
        try {
            const response = await getGuides({
                healthPlanId: parseInt(formData.healthPlanId, 10),
                status: 'GUIDE_AUTHORIZED',
            })
            if (response.success) {
                setAvailableGuides(response.result || [])
            }
        } catch (error) {
            console.error('Erro ao carregar guias:', error)
        } finally {
            setLoadingGuides(false)
        }
    }

    const loadBatch = async () => {
        if (!id) return

        setLoadingData(true)
        try {
            const response = await getBatch(parseInt(id, 10))
            if (response.success && response.result) {
                const batch = response.result
                setFormData({
                    healthPlanId: batch.healthPlanId?.toString() || '',
                    name: batch.name || '',
                    referenceMonth: batch.referenceMonth || '',
                    dueDate: batch.dueDate
                        ? format(new Date(batch.dueDate), 'yyyy-MM-dd')
                        : '',
                    notes: batch.notes || '',
                })
                if (batch.guides) {
                    setSelectedGuideIds(batch.guides.map((g: any) => g.id))
                }
            } else {
                toast.error(response.error || 'Erro ao carregar lote')
                navigate('/billing/batches')
            }
        } catch (error) {
            toast.error('Erro ao carregar lote')
            navigate('/billing/batches')
        } finally {
            setLoadingData(false)
        }
    }

    const updateFormField = (field: keyof FormData, value: string) => {
        setFormData(prev => ({ ...prev, [field]: value }))
    }

    const toggleGuide = (guideId: number) => {
        setSelectedGuideIds(prev =>
            prev.includes(guideId)
                ? prev.filter(id => id !== guideId)
                : [...prev, guideId]
        )
    }

    const selectAllGuides = () => {
        setSelectedGuideIds(filteredGuides.map(g => g.id))
    }

    const deselectAllGuides = () => {
        setSelectedGuideIds([])
    }

    const validateForm = (): boolean => {
        if (!formData.healthPlanId) {
            toast.error('Selecione um convênio')
            return false
        }
        if (!formData.name.trim()) {
            toast.error('Informe o nome do lote')
            return false
        }
        if (!formData.referenceMonth) {
            toast.error('Informe o mes de referencia')
            return false
        }
        if (!isEditing && selectedGuideIds.length === 0) {
            toast.error('Selecione pelo menos uma guia')
            return false
        }
        return true
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!validateForm()) return
        if (!user?.companyId) {
            toast.error('Usuário não autenticado')
            return
        }

        setLoading(true)
        try {
            if (isEditing && id) {
                const updateData: UpdateBillingBatchData = {
                    guideIds: selectedGuideIds,
                }

                const response = await updateBatch(parseInt(id, 10), updateData)
                if (response.success) {
                    toast.success('Lote atualizado com sucesso!')
                    navigate(`/billing/batches/${id}`)
                } else {
                    toast.error(response.error || 'Erro ao atualizar lote')
                }
            } else {
                const [month, year] = formData.referenceMonth
                    .split('/')
                    .map(Number)
                const createData: CreateBillingBatchData = {
                    healthPlanId: parseInt(formData.healthPlanId, 10),
                    competenceMonth: month,
                    competenceYear: year,
                    guideIds: selectedGuideIds,
                }

                const response = await createBatch(createData)
                if (response.success && response.result) {
                    toast.success('Lote criado com sucesso!')
                    navigate(`/billing/batches/${response.result.id}`)
                } else {
                    toast.error(response.error || 'Erro ao criar lote')
                }
            }
        } catch (error) {
            toast.error('Erro ao salvar lote')
        } finally {
            setLoading(false)
        }
    }

    const filteredGuides = availableGuides.filter(guide => {
        if (!searchTerm) return true
        const search = searchTerm.toLowerCase()
        return (
            guide.guideNumber.toLowerCase().includes(search) ||
            guide.client?.name?.toLowerCase().includes(search) ||
            guide.mainOrderName?.toLowerCase().includes(search)
        )
    })

    const formatCurrency = (value: number) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL',
        }).format(value)
    }

    if (loadingData) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando dados do lote...
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6 p-4 max-w-5xl mx-auto">
            <div className="flex items-center gap-3">
                <button
                    onClick={() => navigate('/billing/batches')}
                    className="btn btn-ghost btn-circle btn-sm"
                >
                    <MdArrowBack className="text-xl" />
                </button>
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center shadow-lg">
                        <MdInventory className="text-2xl text-white" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-base-content">
                            {isEditing
                                ? 'Editar Lote'
                                : 'Novo Lote de Faturamento'}
                        </h1>
                        <p className="text-sm text-base-content/60">
                            {isEditing
                                ? 'Atualize as informações do lote'
                                : 'Agrupe guias para envio ao convênio'}
                        </p>
                    </div>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col gap-6">
                {/* Basic Info */}
                <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                    <div className="card-body">
                        <h2 className="card-title text-lg mb-4">
                            Informações do Lote
                        </h2>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Convênio{' '}
                                        <span className="text-error">*</span>
                                    </span>
                                </label>
                                <select
                                    className="select select-bordered w-full"
                                    value={formData.healthPlanId}
                                    onChange={e =>
                                        updateFormField(
                                            'healthPlanId',
                                            e.target.value
                                        )
                                    }
                                    required
                                    disabled={isEditing}
                                >
                                    <option value="">Selecione...</option>
                                    {healthPlans.map(plan => (
                                        <option key={plan.id} value={plan.id}>
                                            {plan.name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Nome do Lote{' '}
                                        <span className="text-error">*</span>
                                    </span>
                                </label>
                                <input
                                    type="text"
                                    className="input input-bordered w-full"
                                    placeholder="Ex: Lote Janeiro 2024"
                                    value={formData.name}
                                    onChange={e =>
                                        updateFormField('name', e.target.value)
                                    }
                                    required
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Mes de Referencia{' '}
                                        <span className="text-error">*</span>
                                    </span>
                                </label>
                                <input
                                    type="month"
                                    className="input input-bordered w-full"
                                    value={formData.referenceMonth}
                                    onChange={e =>
                                        updateFormField(
                                            'referenceMonth',
                                            e.target.value
                                        )
                                    }
                                    required
                                    disabled={isEditing}
                                />
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Data de Vencimento
                                    </span>
                                </label>
                                <input
                                    type="date"
                                    className="input input-bordered w-full"
                                    value={formData.dueDate}
                                    onChange={e =>
                                        updateFormField(
                                            'dueDate',
                                            e.target.value
                                        )
                                    }
                                />
                            </div>
                        </div>

                        <div className="form-control w-full mt-4">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Observações
                                </span>
                            </label>
                            <textarea
                                className="textarea textarea-bordered w-full"
                                placeholder="Observações adicionais..."
                                value={formData.notes}
                                onChange={e =>
                                    updateFormField('notes', e.target.value)
                                }
                                rows={3}
                            />
                        </div>
                    </div>
                </div>

                {/* Guides Selection */}
                {!isEditing && formData.healthPlanId && (
                    <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                        <div className="card-body">
                            <div className="flex items-center justify-between mb-4">
                                <h2 className="card-title text-lg">
                                    Selecionar Guias{' '}
                                    <span className="text-error">*</span>
                                    <span className="badge badge-secondary ml-2">
                                        {selectedGuideIds.length} selecionadas
                                    </span>
                                </h2>
                                <div className="flex items-center gap-2">
                                    <div className="relative">
                                        <MdSearch className="absolute z-10 left-3 top-1/2 -translate-y-1/2 text-base-content/40" />
                                        <input
                                            type="text"
                                            placeholder="Buscar guias..."
                                            value={searchTerm}
                                            onChange={e =>
                                                setSearchTerm(e.target.value)
                                            }
                                            className="input input-bordered input-sm pl-9 w-64"
                                        />
                                    </div>
                                    <button
                                        type="button"
                                        onClick={selectAllGuides}
                                        className="btn btn-ghost btn-sm"
                                    >
                                        Selecionar Todas
                                    </button>
                                    <button
                                        type="button"
                                        onClick={deselectAllGuides}
                                        className="btn btn-ghost btn-sm"
                                    >
                                        Limpar
                                    </button>
                                </div>
                            </div>

                            {loadingGuides ? (
                                <div className="flex justify-center py-8">
                                    <span className="loading loading-spinner loading-md"></span>
                                </div>
                            ) : filteredGuides.length === 0 ? (
                                <div className="text-center py-8 text-base-content/60">
                                    <MdInventory className="text-4xl mx-auto mb-2 opacity-40" />
                                    <p>Nenhuma guia autorizada disponível</p>
                                    <p className="text-sm mt-1">
                                        Selecione um convênio e verifique se há
                                        guias autorizadas
                                    </p>
                                </div>
                            ) : (
                                <div className="overflow-x-auto max-h-96">
                                    <table className="table table-sm table-pin-rows">
                                        <thead>
                                            <tr>
                                                <th className="w-10">
                                                    <input
                                                        type="checkbox"
                                                        className="checkbox checkbox-sm"
                                                        checked={
                                                            selectedGuideIds.length ===
                                                            filteredGuides.length
                                                        }
                                                        onChange={e =>
                                                            e.target.checked
                                                                ? selectAllGuides()
                                                                : deselectAllGuides()
                                                        }
                                                    />
                                                </th>
                                                <th>Guia</th>
                                                <th>Paciente</th>
                                                <th>Procedimento</th>
                                                <th className="text-right">
                                                    Valor
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredGuides.map(guide => (
                                                <tr
                                                    key={guide.id}
                                                    className={`hover cursor-pointer ${selectedGuideIds.includes(guide.id) ? 'bg-secondary/10' : ''}`}
                                                    onClick={() =>
                                                        toggleGuide(guide.id)
                                                    }
                                                >
                                                    <td>
                                                        <input
                                                            type="checkbox"
                                                            className="checkbox checkbox-sm checkbox-secondary"
                                                            checked={selectedGuideIds.includes(
                                                                guide.id
                                                            )}
                                                            onChange={() =>
                                                                toggleGuide(
                                                                    guide.id
                                                                )
                                                            }
                                                            onClick={e =>
                                                                e.stopPropagation()
                                                            }
                                                        />
                                                    </td>
                                                    <td className="font-mono text-sm">
                                                        {guide.guideNumber}
                                                    </td>
                                                    <td>
                                                        {guide.client?.name ||
                                                            '-'}
                                                    </td>
                                                    <td className="text-sm">
                                                        {guide.mainOrderName ||
                                                            '-'}
                                                    </td>
                                                    <td className="text-right font-semibold">
                                                        {formatCurrency(
                                                            guide.totalAuthorized ||
                                                                guide.totalRequested
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {selectedGuideIds.length > 0 && (
                                <div className="mt-4 p-3 bg-secondary/10 rounded-lg flex items-center justify-between">
                                    <span className="font-semibold">
                                        Total do Lote:
                                    </span>
                                    <span className="text-xl font-bold text-secondary">
                                        {formatCurrency(
                                            filteredGuides
                                                .filter(g =>
                                                    selectedGuideIds.includes(
                                                        g.id
                                                    )
                                                )
                                                .reduce(
                                                    (sum, g) =>
                                                        sum +
                                                        (g.totalAuthorized ||
                                                            g.totalRequested),
                                                    0
                                                )
                                        )}
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                <div className="flex justify-end gap-3">
                    <button
                        type="button"
                        onClick={() => navigate('/billing/batches')}
                        className="btn btn-ghost"
                        disabled={loading}
                    >
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        className="btn btn-secondary gap-2"
                        disabled={loading}
                    >
                        {loading ? (
                            <>
                                <span className="loading loading-spinner loading-sm"></span>
                                Salvando...
                            </>
                        ) : (
                            <>
                                <MdSave className="text-lg" />
                                Salvar
                            </>
                        )}
                    </button>
                </div>
            </form>
        </div>
    )
}
